import { useUserName } from "./hooks";
import { useAppSelector } from "@hooks/redux";
import { Divider, Popover } from "antd";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import UserAvatar from "@components/AvatarUpload/UserAvatar";
import UploadImage from "@components/UploadImage";
import { useLogout } from "@hooks/account";
import useUserInfo from "@hooks/userInfo";
import { MembershipMapStyle } from "@hooks/membership";
import { useState } from "react";
import useMemberInfo from "@hooks/useMember";
import LanguagePopover from "../LanguagePopover";
import { ThemeToggleButton } from "@components/ThemeToggleButton";
import { IconOut, IconSun, IconMoon, IconSetting, IconExpand } from "@icons/index";
import { IconLineCustomerService } from "@kalodata/kds-design-icons";
import { themeAtom } from "@src/jotai/theme";
import { contactUsModalOpenAtom } from "@src/jotai/contactUs";
import { ContactUsModal } from "@components/ContactUsModal/ContactUsModal";
import { useAtomValue, useSetAtom } from "jotai";

export const AccountDrawer = ({ isOpen }: { isOpen: boolean }) => {
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const userName = useUserName();
  const { isLogin } = useAppSelector((state) => state.globalReducer);

  if (!isLogin) {
    return (
      <div className="w-full">
        <UploadImage className="w-[36px] h-[36px] shrink-0 text-[20px]" name="f6e58ca0.png"></UploadImage>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ContactUsModal />
      <Popover
        content={<AccountDrawerContent onClose={() => setAccountDrawerOpen(false)}></AccountDrawerContent>}
        arrow={false}
        open={accountDrawerOpen}
        onOpenChange={setAccountDrawerOpen}
        trigger={"hover"}
        placement="rightBottom"
        align={{ offset: [12, 6] }}
      >
        {isOpen ? (
          <div className="flex items-center justify-between gap-[8px] p-[6px] rounded-[8px] cursor-pointer hover:bg-kds-bg-overlay">
            <div className="flex items-center gap-[8px] min-w-0">
              <UserAvatar className="w-[32px] h-[32px] shrink-0 text-[16px]"></UserAvatar>
              <span className="text-[14px] text-kds-fg truncate">{userName || "-"}</span>
            </div>
            <IconExpand className="w-[20px] h-[20px] shrink-0 text-kds-fg-soft" />
          </div>
        ) : (
          <div className="flex justify-center">
            <UserAvatar className="w-[32px] h-[32px] shrink-0 text-[16px]"></UserAvatar>
          </div>
        )}
      </Popover>
    </div>
  );
};

const AccountDrawerContent = ({ onClose }: { onClose: () => void }) => {
  const { userInfo } = useUserInfo();
  const userName = useUserName();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const { language, isLogin } = useAppSelector((state) => state.globalReducer);
  const { languageOptions } = useAppSelector((state) => state.configReducer);
  const isDark = useAtomValue(themeAtom) === "dark";
  const setContactUsModalOpen = useSetAtom(contactUsModalOpenAtom);

  useMemberInfo();

  const currentLanguageLabel = languageOptions.find((option) => option.value === language)?.label || "EN";

  // TODO: 暂时写死 free 版本
  const level = "free";

  const goSettings = () => {
    isLogin ? navigate("/me") : navigate("/login");
    onClose();
  };

  return (
    <div className="w-[260px] flex flex-col p-[8px] text-base">
      {/* 头部：会员标签 + 账户信息 */}
      <div className="flex flex-col gap-[8px] pb-[8px] border-b border-kds-border">
        <div className="flex items-center">
          <span
            className="inline-flex items-center h-[20px] leading-[20px] px-[8px] rounded-[4px] text-[12px] font-bold"
            style={{
              backgroundColor: MembershipMapStyle?.[level]?.backgroundColor,
              color: MembershipMapStyle?.[level]?.textColor,
            }}
          >
            <Trans>{MembershipMapStyle?.[level]?.text}</Trans>
          </span>
        </div>
        <div className="flex items-center gap-[8px] cursor-pointer" onClick={goSettings}>
          <UserAvatar className="w-[40px] h-[40px] shrink-0 text-[20px]"></UserAvatar>
          <div className="flex flex-col justify-center min-w-0 text-kds-fg">
            <div className="text-[14px] font-semibold line-clamp-1">{userName ? userName : "-"}</div>
            <div className="text-[12px] text-kds-fg-soft line-clamp-1">{userInfo.username}</div>
          </div>
        </div>
      </div>

      {/* 菜单项 */}
      <div className="flex flex-col pt-[8px]">
        <div
          className="flex items-center gap-[6px] h-[36px] p-[8px] rounded-[8px] cursor-pointer hover:bg-kds-bg-overlay"
          onClick={goSettings}
        >
          <IconSetting className="w-[16px] h-[16px]" />
          <span className="text-[14px] text-kds-fg">
            <Trans>Settings</Trans>
          </span>
        </div>

        <LanguagePopover currentLanguageLabel={currentLanguageLabel} onClose={onClose} offset={[-12, -10]} />

        <div className="flex items-center justify-between gap-[6px] h-[36px] p-[8px] rounded-[8px]">
          <div className="flex items-center gap-[6px]">
            {isDark ? <IconMoon className="w-[16px] h-[16px]" /> : <IconSun className="w-[16px] h-[16px]" />}
            <span className="text-[14px] text-kds-fg">
              <Trans>Theme</Trans>
            </span>
          </div>
          <ThemeToggleButton />
        </div>

        <div
          className="flex items-center gap-[6px] h-[36px] p-[8px] rounded-[8px] cursor-pointer hover:bg-kds-bg-overlay"
          onClick={() => {
            setContactUsModalOpen(true);
            onClose();
          }}
        >
          <IconLineCustomerService size={16} />
          <span className="text-[14px] text-kds-fg">
            <Trans>Contact Us</Trans>
          </span>
        </div>

        <Divider style={{ margin: "4px 0" }} />

        <div
          className="flex items-center gap-[6px] h-[36px] p-[8px] rounded-[8px] cursor-pointer hover:bg-kds-bg-overlay"
          onClick={logout}
        >
          <IconOut className="w-[16px] h-[16px]" />
          <span className="text-[14px] text-kds-fg">
            <Trans>Log out</Trans>
          </span>
        </div>
      </div>
    </div>
  );
};
